import { useState, useRef, useEffect } from 'react';
import { useBetSlip } from '../../../components/feature/BetSlipContext';
import PageHero from '../../../components/feature/PageHero';

// ─── Types ────────────────────────────────────────────────────────────────────

type MarketType = 'MONEYLINE' | 'SPREAD' | 'TOTAL';
type Sport = 'NFL' | 'NBA';

interface BetOption {
  id: string;
  marketType: MarketType;
  sport: Sport;
  matchup: string;
  pick: string;
  betToWin: string;
  payout: string;
}

// ─── Bet Data ─────────────────────────────────────────────────────────────────

const ALL_BETS: BetOption[] = [
  // NFL Moneylines
  { id: 'ml-nfl-1a', marketType: 'MONEYLINE', sport: 'NFL', matchup: '49ers vs Eagles', pick: '49ers', betToWin: '$1 to win', payout: '$0.67' },
  { id: 'ml-nfl-1b', marketType: 'MONEYLINE', sport: 'NFL', matchup: '49ers vs Eagles', pick: 'Eagles', betToWin: '$1 to win', payout: '$1.30' },
  { id: 'ml-nfl-2a', marketType: 'MONEYLINE', sport: 'NFL', matchup: 'Cowboys vs Packers', pick: 'Cowboys', betToWin: '$1 to win', payout: '$0.56' },
  { id: 'ml-nfl-2b', marketType: 'MONEYLINE', sport: 'NFL', matchup: 'Cowboys vs Packers', pick: 'Packers', betToWin: '$1 to win', payout: '$1.55' },
  { id: 'ml-nfl-3a', marketType: 'MONEYLINE', sport: 'NFL', matchup: 'Bengals vs Steelers', pick: 'Bengals', betToWin: '$1 to win', payout: '$0.80' },
  { id: 'ml-nfl-3b', marketType: 'MONEYLINE', sport: 'NFL', matchup: 'Bengals vs Steelers', pick: 'Steelers', betToWin: '$1 to win', payout: '$1.05' },
  // NBA Totals
  { id: 'tot-nba-1a', marketType: 'TOTAL', sport: 'NBA', matchup: 'Lakers vs Celtics', pick: 'Over 225.5', betToWin: '$1 to win', payout: '$0.87' },
  { id: 'tot-nba-1b', marketType: 'TOTAL', sport: 'NBA', matchup: 'Lakers vs Celtics', pick: 'Under 225.5', betToWin: '$1 to win', payout: '$0.95' },
  { id: 'tot-nba-2a', marketType: 'TOTAL', sport: 'NBA', matchup: 'Warriors vs Nuggets', pick: 'Over 230.5', betToWin: '$1 to win', payout: '$0.91' },
  { id: 'tot-nba-2b', marketType: 'TOTAL', sport: 'NBA', matchup: 'Warriors vs Nuggets', pick: 'Under 230.5', betToWin: '$1 to win', payout: '$0.91' },
  // NFL Spreads
  { id: 'sp-nfl-1a', marketType: 'SPREAD', sport: 'NFL', matchup: '49ers vs Eagles', pick: '49ers -3.5', betToWin: '$1 to win', payout: '$0.92' },
  { id: 'sp-nfl-1b', marketType: 'SPREAD', sport: 'NFL', matchup: '49ers vs Eagles', pick: 'Eagles +3.5', betToWin: '$1 to win', payout: '$1.08' },
  { id: 'sp-nfl-2a', marketType: 'SPREAD', sport: 'NFL', matchup: 'Cowboys vs Packers', pick: 'Cowboys -2.5', betToWin: '$1 to win', payout: '$0.97' },
  { id: 'sp-nfl-2b', marketType: 'SPREAD', sport: 'NFL', matchup: 'Cowboys vs Packers', pick: 'Packers +2.5', betToWin: '$1 to win', payout: '$1.03' },
  // NBA Moneylines
  { id: 'ml-nba-1a', marketType: 'MONEYLINE', sport: 'NBA', matchup: 'Lakers vs Celtics', pick: 'Lakers', betToWin: '$1 to win', payout: '$0.72' },
  { id: 'ml-nba-1b', marketType: 'MONEYLINE', sport: 'NBA', matchup: 'Lakers vs Celtics', pick: 'Celtics', betToWin: '$1 to win', payout: '$1.18' },
  { id: 'ml-nba-2a', marketType: 'MONEYLINE', sport: 'NBA', matchup: 'Warriors vs Nuggets', pick: 'Warriors', betToWin: '$1 to win', payout: '$0.88' },
  { id: 'ml-nba-2b', marketType: 'MONEYLINE', sport: 'NBA', matchup: 'Warriors vs Nuggets', pick: 'Nuggets', betToWin: '$1 to win', payout: '$1.02' },
];

// ─── Badge Colors ─────────────────────────────────────────────────────────────

const MARKET_BADGE: Record<MarketType, { bg: string; text: string }> = {
  MONEYLINE: { bg: 'bg-slate-700', text: 'text-slate-200' },
  SPREAD:    { bg: 'bg-slate-700', text: 'text-slate-200' },
  TOTAL:     { bg: 'bg-teal-700/60', text: 'text-teal-200' },
};

const SPORT_BADGE: Record<Sport, { bg: string; text: string }> = {
  NFL: { bg: 'bg-amber-500/20', text: 'text-amber-400' },
  NBA: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
};

// ─── Bet Card ─────────────────────────────────────────────────────────────────

function BetCard({ bet, selected, onToggle }: { bet: BetOption; selected: boolean; onToggle: () => void }) {
  const mBadge = MARKET_BADGE[bet.marketType];
  const sBadge = SPORT_BADGE[bet.sport];
  const [spreadAdj, setSpreadAdj] = useState(0);

  // Parse spread value from pick string e.g. "49ers -3.5" → -3.5
  const getAdjustedPick = () => {
    if (bet.marketType !== 'SPREAD') return bet.pick;
    const match = bet.pick.match(/([+-]?\d+(\.\d+)?)$/);
    if (!match) return bet.pick;
    const base = parseFloat(match[1]);
    const newVal = base + spreadAdj;
    const newStr = newVal >= 0 ? `+${newVal.toFixed(1)}` : `${newVal.toFixed(1)}`;
    return bet.pick.replace(/([+-]?\d+(\.\d+)?)$/, newStr);
  };

  const getAdjustedPayout = () => {
    if (bet.marketType !== 'SPREAD') return bet.payout;
    const base = parseFloat(bet.payout.replace('$', ''));
    // Each 0.5 shift changes payout slightly
    const adjusted = base + spreadAdj * 0.04;
    return `$${Math.max(0.10, adjusted).toFixed(2)}`;
  };

  const adjSteps = Math.round(spreadAdj / 0.5);
  const minSteps = -6;
  const maxSteps = 6;

  return (
    <div
      onClick={bet.marketType !== 'SPREAD' ? onToggle : undefined}
      className={`rounded-xl border p-3 transition-all select-none ${
        bet.marketType === 'SPREAD' ? 'cursor-default' : 'cursor-pointer'
      } ${
        selected
          ? 'border-teal-500/70 bg-teal-500/10'
          : 'border-slate-700/80 hover:border-slate-500/80'
      }`}
      style={{ background: selected ? undefined : '#1e2330' }}
    >
      {/* Badges row */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${mBadge.bg} ${mBadge.text} uppercase tracking-wide`}>
          {bet.marketType}
        </span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${sBadge.bg} ${sBadge.text} uppercase tracking-wide`}>
          {bet.sport}
        </span>
      </div>

      {/* Matchup */}
      <div className="text-[11px] text-slate-400 mb-0.5 truncate">{bet.matchup}</div>

      {/* Pick + payout inline */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className={`text-sm font-bold truncate ${selected ? 'text-teal-300' : 'text-white'}`}>
          {getAdjustedPick()}
        </div>
        <div className="text-xs font-bold flex-shrink-0 text-teal-400">
          {getAdjustedPayout()}
        </div>
      </div>

      {/* Spread Adjuster — only for SPREAD type */}
      {bet.marketType === 'SPREAD' && (
        <div className="mt-2 bg-slate-900/60 rounded-lg border border-slate-700/50 px-2.5 py-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold text-slate-400 flex items-center gap-1">
              <i className="ri-equalizer-line text-teal-400 text-[10px]"></i>
              Adjust Spread
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              spreadAdj === 0
                ? 'bg-slate-700 text-slate-400'
                : spreadAdj > 0
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'bg-rose-500/20 text-rose-400'
            }`}>
              {spreadAdj === 0 ? 'Market' : spreadAdj > 0 ? `+${spreadAdj.toFixed(1)}` : `${spreadAdj.toFixed(1)}`}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={e => { e.stopPropagation(); setSpreadAdj(a => Math.max(minSteps * 0.5, a - 0.5)); }}
              disabled={adjSteps <= minSteps}
              className="w-5 h-5 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-600 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-subtract-line text-[10px]"></i>
            </button>
            <input
              type="range"
              min={minSteps}
              max={maxSteps}
              step={1}
              value={adjSteps}
              onClick={e => e.stopPropagation()}
              onChange={e => { e.stopPropagation(); setSpreadAdj(parseInt(e.target.value) * 0.5); }}
              className="flex-1 h-1 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${((adjSteps - minSteps) / (maxSteps - minSteps)) * 100}%, #334155 ${((adjSteps - minSteps) / (maxSteps - minSteps)) * 100}%, #334155 100%)`,
                accentColor: '#14b8a6',
              }}
            />
            <button
              onClick={e => { e.stopPropagation(); setSpreadAdj(a => Math.min(maxSteps * 0.5, a + 0.5)); }}
              disabled={adjSteps >= maxSteps}
              className="w-5 h-5 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-600 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-add-line text-[10px]"></i>
            </button>
          </div>
        </div>
      )}

      {/* Click to select — only for SPREAD */}
      {bet.marketType === 'SPREAD' && (
        <button
          onClick={onToggle}
          className={`mt-2 w-full text-[10px] font-semibold py-1 rounded-md border transition-all cursor-pointer whitespace-nowrap ${
            selected
              ? 'bg-teal-500/20 border-teal-500/40 text-teal-400'
              : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'
          }`}
        >
          {selected ? <><i className="ri-checkbox-circle-fill mr-1"></i>Added to parlay</> : 'Select for Parlay'}
        </button>
      )}

      {selected && bet.marketType !== 'SPREAD' && (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-teal-400 font-semibold">
          <i className="ri-checkbox-circle-fill text-xs"></i>
          Added to parlay
        </div>
      )}
    </div>
  );
}

// ─── Nav Config ───────────────────────────────────────────────────────────────

const SUBNAV = [
  { key: 'all',          label: 'All',          icon: 'ri-apps-2-line',            href: '/markets' },
  { key: 'spreads',      label: 'Spreads',       icon: 'ri-bar-chart-grouped-line', href: '/markets/spreads' },
  { key: 'money-lines',  label: 'Money Lines',   icon: 'ri-copper-coin-line',       href: '/markets/money-lines' },
  { key: 'futures',      label: 'Futures',       icon: 'ri-calendar-event-line',    href: '/markets/futures' },
  { key: 'parlays',      label: 'Parlays',       icon: 'ri-stack-line',             href: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props',  icon: 'ri-user-star-line',         href: '/markets/player-props' },
];

const marketSubLinks = [
  { href: '/markets/spreads',      icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
  { href: '/markets/money-lines',  icon: 'ri-copper-coin-line',       label: 'Money Lines' },
  { href: '/markets/futures',      icon: 'ri-calendar-event-line',    label: 'Futures' },
  { href: '/markets/parlays',      icon: 'ri-stack-line',             label: 'Parlays' },
  { href: '/markets/player-props', icon: 'ri-user-star-line',         label: 'Player Props' },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketsParlaysPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const { items: slipItems, toggleSlip, addBet } = useBetSlip();

  // Filters
  const [sportFilter, setSportFilter] = useState<'All Sports' | 'NFL' | 'NBA'>('All Sports');
  const [typeFilter, setTypeFilter] = useState<'All Types' | 'Spreads' | 'Moneyline' | 'Totals'>('All Types');

  // Parlay legs
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-parlays';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Parlays — L2L Sports Prediction Market',
      description: 'Build multi-leg parlay bets on NFL and NBA games. Combine spreads, money lines, and totals with peer-to-peer pricing.',
      url: `${siteUrl}/markets/parlays`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Markets', item: `${siteUrl}/markets` },
          { '@type': 'ListItem', position: 3, name: 'Parlays', item: `${siteUrl}/markets/parlays` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const toggleLeg = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  // Filter bets
  const filteredBets = ALL_BETS.filter(b => {
    const sportOk = sportFilter === 'All Sports' || b.sport === sportFilter;
    const typeMap: Record<string, MarketType[]> = {
      'All Types': ['MONEYLINE', 'SPREAD', 'TOTAL'],
      'Spreads':   ['SPREAD'],
      'Moneyline': ['MONEYLINE'],
      'Totals':    ['TOTAL'],
    };
    const typeOk = typeMap[typeFilter].includes(b.marketType);
    return sportOk && typeOk;
  });

  // Selected legs data
  const selectedLegs = ALL_BETS.filter(b => selectedIds.has(b.id));

  // Combined payout multiplier (simplified: sum of payout values)
  const combinedPayout = selectedLegs.reduce((acc, leg) => {
    const val = parseFloat(leg.payout.replace('$', ''));
    return acc + val;
  }, 0);

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>

      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>

            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(o => !o)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    {marketSubLinks.map(link => (
                      <a key={link.href} href={link.href} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${link.href === '/markets/parlays' ? 'bg-teal-500/10 text-teal-400' : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'}`}>
                          <i className={`${link.icon} w-4 h-4 flex items-center justify-center ${link.href === '/markets/parlays' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                          {link.label}
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-apps-2-line w-4 h-4 flex items-center justify-center text-slate-500"></i>
                          All Markets
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button
                onClick={toggleSlip}
                className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipItems.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{slipItems.length}</span>}
              </button>
              <a href="/my-account"><div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"><i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i></div></a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'All Markets' },
                { href: '/markets/spreads', icon: 'ri-bar-chart-grouped-line', label: 'Spreads' },
                { href: '/markets/money-lines', icon: 'ri-copper-coin-line', label: 'Money Lines' },
                { href: '/markets/futures', icon: 'ri-calendar-event-line', label: 'Futures' },
                { href: '/markets/parlays', icon: 'ri-stack-line', label: 'Parlays' },
                { href: '/markets/player-props', icon: 'ri-user-star-line', label: 'Player Props' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        title="Parlays"
        subtitle="Combine multiple bets into one — multiply your payout by stacking winning picks"
        badgeLabel="Live Parlays Markets"
        badgeColor="bg-violet-500/20 border-violet-500/40 text-violet-400"
        backgroundImage="https://public.readdy.ai/ai/img_res/817eeb467f711cd9771d662f71a53034.jpg"
      />

      {/* Sub Navigation */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {SUBNAV.map(sub => (
              <a key={sub.key} href={sub.href}>
                <div className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  sub.key === 'parlays'
                    ? 'bg-slate-700 text-white border border-slate-500/60'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                }`}>
                  <i className={`${sub.icon} text-sm ${sub.key === 'parlays' ? 'text-teal-400' : 'text-slate-500'}`}></i>
                  {sub.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6 items-start">

            {/* ── Left: Available Bets ── */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-white mb-5">Available Bets</h2>

              {/* Filter Rows */}
              <div className="flex flex-wrap gap-2 mb-3">
                {(['All Sports', 'NFL', 'NBA'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setSportFilter(s)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                      sportFilter === s
                        ? 'bg-teal-500 border-teal-500 text-white shadow-lg shadow-teal-500/20'
                        : 'bg-transparent border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {(['All Types', 'Spreads', 'Moneyline', 'Totals'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                      typeFilter === t
                        ? 'bg-teal-500 border-teal-500 text-white shadow-lg shadow-teal-500/20'
                        : 'bg-transparent border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Bet Cards Grid */}
              {filteredBets.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredBets.map(bet => (
                    <BetCard
                      key={bet.id}
                      bet={bet}
                      selected={selectedIds.has(bet.id)}
                      onToggle={() => toggleLeg(bet.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-slate-500">
                  <i className="ri-stack-line text-4xl mb-3 block"></i>
                  <p>No bets match the selected filters.</p>
                </div>
              )}
            </div>

            {/* ── Right: Parlay Calculator ── */}
            <div className="lg:col-span-1">
              <div className="sticky top-[105px]">
                <div className="rounded-2xl border border-slate-700/80 overflow-hidden" style={{ background: '#1a1f2e' }}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">
                    <div className="flex items-center gap-2">
                      <i className="ri-calculator-line text-teal-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                      <span className="text-white font-bold text-base">Parlay Calculator</span>
                    </div>
                    {selectedLegs.length > 0 && (
                      <button
                        onClick={clearAll}
                        className="text-xs text-slate-500 hover:text-rose-400 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="px-5 py-4">
                    <p className="text-slate-400 text-sm mb-4">
                      Selected Legs ({selectedLegs.length})
                    </p>

                    {selectedLegs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-slate-800/60 mb-4">
                          <i className="ri-stack-line text-3xl text-slate-600"></i>
                        </div>
                        <p className="text-slate-500 text-sm">Select bets to build your parlay</p>
                      </div>
                    ) : (
                      <>
                        {/* Legs list */}
                        <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
                          {selectedLegs.map(leg => (
                            <div key={leg.id} className="flex items-start justify-between gap-2 bg-slate-800/50 rounded-lg px-3 py-2.5 border border-slate-700/50">
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] text-slate-500 truncate mb-0.5">{leg.matchup}</div>
                                <div className="text-sm font-semibold text-white truncate">{leg.pick}</div>
                                <div className="text-[10px] text-teal-400 font-bold mt-0.5">Payout: {leg.payout}</div>
                              </div>
                              <button
                                onClick={() => toggleLeg(leg.id)}
                                className="text-slate-600 hover:text-rose-400 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Summary */}
                        <div className="bg-slate-900/60 rounded-xl border border-slate-700/60 p-4 mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-slate-400 text-sm">Legs</span>
                            <span className="text-white font-bold">{selectedLegs.length}</span>
                          </div>
                          <div className="flex items-center justify-between mb-3 pt-2 border-t border-slate-700/50">
                            <span className="text-slate-400 text-sm">Payout</span>
                            <span className="text-teal-400 font-extrabold text-lg">${combinedPayout.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Place Parlay */}
                        <button
                          onClick={() => {
                            selectedLegs.forEach(leg => {
                              addBet({
                                id: `parlay-${leg.id}`,
                                market: `Parlay — ${leg.marketType}`,
                                matchup: leg.matchup,
                                pick: leg.pick,
                                price: '$1.00',
                                payout: leg.payout,
                                profit: leg.payout,
                                badge: leg.sport,
                                badgeColor: leg.sport === 'NFL'
                                  ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                  : 'bg-orange-500/20 text-orange-400 border-orange-500/30',
                              });
                            });
                            clearAll();
                          }}
                          className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold rounded-lg shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap text-sm"
                        >
                          <i className="ri-check-line"></i>
                          Add Parlay to Slip
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4 mt-8" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
